import type { Action, Competition, Group, GroupsConfig, Id, Match, Phase, PhaseConfig, Team, Tournament } from '../../types.js';
import { createRng } from '../../rng.js';
import { nextId } from '../../ids.js';
import { roundRobin } from '../../schedule/roundRobin.js';
import { partnerLayoutSpec, partnerSchedule } from '../../schedule/partner.js';
import { computeStandings } from '../../standings/standings.js';
import { placementTier } from '../../standings/placement.js';
import { normalizeRules } from '../../rules/validate.js';
import { eligibleTeams, rankTeams } from '../../seeding/seeding.js';
import { distribute, groupLayout, repairConstraints } from '../../seeding/groups.js';
import { buildSingleElim, placeQualifiers, type Qualifier } from '../../bracket/single.js';
import { EngineError } from '../state.js';
import { clone } from '../../clone.js';
import { clubKey, pairingKey, teamKey, teamNumber } from '../keys.js';
import { competition, phaseOf, rulesOf, teamOf } from './shared.js';
import { autoWalkovers } from './matches.js';

export const ALGORITHM_VERSION = '1';

// ---------------------------------------------------------------------------
// Phasen und Gruppen
// ---------------------------------------------------------------------------

export function applyPhase(t: Tournament, action: Action): string[] {
  const warnings: string[] = [];
  switch (action.type) {
    case 'phase.add': {
      const comp = competition(t, action.competitionId);
      validatePhaseConfig(comp, action.config);
      const phase: Phase = {
        id: nextId(t, 'ph'),
        competitionId: comp.id,
        type: action.config.type,
        order: comp.phases.length + 1,
        status: 'planned',
        config: clone(action.config),
        groups: [],
        matches: [],
        frozen: false,
      };
      if (action.ruleSetOverride) phase.ruleSetOverride = normalizeRules(clone(action.ruleSetOverride));
      comp.phases.push(phase);
      break;
    }
    case 'phase.remove': {
      const { comp, phase } = phaseOf(t, action.phaseId);
      if (phase.status !== 'planned') throw new EngineError('Phase läuft schon oder ist abgeschlossen');
      if (phase.matches.length > 0) throw new EngineError('Phase hat schon Spiele');
      comp.phases = comp.phases.filter((p) => p.id !== phase.id);
      // Reihenfolge lückenlos halten, sonst steht später „Phase 3" ohne Phase 2 da
      comp.phases.forEach((p, i) => (p.order = i + 1));
      warnings.push(`Phase ${phase.order} entfernt`);
      break;
    }
    case 'phase.updateConfig': {
      const { comp, phase } = phaseOf(t, action.phaseId);
      if (phase.status === 'running' || phase.status === 'finished') throw new EngineError('Phase läuft schon');
      if (action.config.type !== phase.type) throw new EngineError('Phasentyp kann nicht geändert werden — Phase neu anlegen');
      validatePhaseConfig(comp, action.config);
      phase.config = clone(action.config);
      if (phase.status === 'drawn') {
        resetDraw(phase);
        warnings.push('Auslosung verworfen — bitte neu auslosen');
      }
      break;
    }
    case 'phase.setOptions':
      warnings.push(...setOptions(t, action.phaseId, action.patch));
      break;
    case 'phase.setCourtGroup': {
      const { phase } = phaseOf(t, action.phaseId);
      const ort = action.courtGroup?.trim() || undefined;
      if (ort && !t.courts.some((c) => c.group === ort)) throw new EngineError(`Platz ${ort} hat kein Feld`);
      if (action.roundId) {
        const runde = phase.koRounds?.find((r) => r.id === action.roundId);
        if (!runde) throw new EngineError('KO-Runde nicht gefunden');
        if (ort) runde.courtGroup = ort;
        else delete runde.courtGroup;
      } else if (ort) phase.config.courtGroup = ort;
      else delete phase.config.courtGroup;
      // Laufende Spiele auf einem anderen Platz werden nicht abgeräumt — sie
      // spielen fertig. Die Bindung gilt für alles, was danach zugeteilt wird.
      const laufend = phase.matches.filter((m) => m.status === 'running' && m.courtId && t.courts.find((c) => c.id === m.courtId)?.group !== ort);
      if (ort && laufend.length > 0)
        warnings.push(
          `${laufend.length} laufende${laufend.length === 1 ? 's' : ''} Spiel${laufend.length === 1 ? '' : 'e'} steh${laufend.length === 1 ? 't' : 'en'} auf einem anderen Platz — sie werden fertig gespielt`,
        );
      break;
    }
    case 'phase.draw':
      warnings.push(...drawPhase(t, action.phaseId, action.seed));
      break;
    case 'phase.moveTeam': {
      const { comp, phase } = phaseOf(t, action.phaseId);
      if (phase.type !== 'groups') throw new EngineError('Nur in Gruppenphasen möglich');
      if (phase.frozen) throw new EngineError('Auslosung ist eingefroren');
      if (phase.matches.some((m) => m.status !== 'ready' && m.status !== 'open')) throw new EngineError('Es gibt schon Ergebnisse — Verschieben nicht mehr möglich');
      const from = phase.groups.find((g) => g.teamIds.includes(action.teamId));
      const to = phase.groups.find((g) => g.id === action.toGroupId);
      if (!from || !to) throw new EngineError('Gruppe nicht gefunden');
      if (from.id === to.id) break;
      from.teamIds = from.teamIds.filter((id) => id !== action.teamId);
      to.teamIds.push(action.teamId);
      phase.matches = [];
      t.counters[`m:${phase.id}`] = 0;
      for (const g of phase.groups) phase.matches.push(...groupMatches(t, comp, phase, g, (phase.config as GroupsConfig).doubleRound));
      renumber(phase);
      warnings.push(...groupWarnings(comp, phase));
      break;
    }
    case 'phase.freeze': {
      const { phase } = phaseOf(t, action.phaseId);
      if (phase.status === 'planned') throw new EngineError('Erst auslosen');
      phase.frozen = true;
      break;
    }
    case 'phase.finish':
      warnings.push(...finishPhase(t, action.phaseId, action.force ?? false));
      break;
    case 'group.resolveLot': {
      const { phase } = phaseOf(t, action.phaseId);
      const group = phase.groups.find((g) => g.id === action.groupId);
      if (!group) throw new EngineError('Gruppe nicht gefunden');
      group.lotOrder = Object.fromEntries(action.order.map((id, i) => [id, i + 1]));
      break;
    }
    case 'group.setCourtGroup': {
      const { phase } = phaseOf(t, action.phaseId);
      const group = phase.groups.find((g) => g.id === action.groupId);
      if (!group) throw new EngineError('Gruppe nicht gefunden');
      const cg = action.courtGroup?.trim();
      if (cg) group.courtGroup = cg;
      else delete group.courtGroup;
      break;
    }
  }
  return warnings;
}

function setOptions(t: Tournament, phaseId: Id, patch: { checkedInOnly?: boolean; qualifiersPerGroup?: number; bestRemaining?: number; thirdPlaceMatch?: boolean }): string[] {
  const { comp, phase } = phaseOf(t, phaseId);
  const warnings: string[] = [];
  const zahl = (v: unknown, name: string): number => {
    if (typeof v !== 'number' || !Number.isInteger(v) || v < 0 || v > 64) throw new EngineError(`${name}: ganze Zahl von 0 bis 64`);
    return v;
  };
  if (phase.config.type === 'groups') {
    const cfg = phase.config;
    if (patch.qualifiersPerGroup !== undefined || patch.bestRemaining !== undefined) {
      // Bis das KO ausgelost ist — danach stehen die Qualifikanten im Baum
      const folge = comp.phases.find((p) => p.order === phase.order + 1);
      if (folge && folge.status !== 'planned') throw new EngineError('Die KO-Runde ist schon ausgelost — Qualifikanten nicht mehr änderbar');
      if (patch.qualifiersPerGroup !== undefined) cfg.qualifiersPerGroup = zahl(patch.qualifiersPerGroup, 'Weiter je Gruppe');
      if (patch.bestRemaining !== undefined) cfg.bestRemaining = zahl(patch.bestRemaining, 'Beste Nächstplatzierte');
    }
    if (patch.checkedInOnly !== undefined) {
      if (phase.frozen) throw new EngineError('Auslosung ist eingefroren');
      if (patch.checkedInOnly) cfg.checkedInOnly = true;
      else delete cfg.checkedInOnly;
      if (phase.status === 'drawn') warnings.push('Gilt ab dem nächsten Neu-Würfeln');
    }
  } else if (patch.thirdPlaceMatch !== undefined) {
    const cfg = phase.config;
    if (phase.status === 'finished') throw new EngineError('Phase ist abgeschlossen');
    const third = phase.bracket?.thirdPlaceMatchId ? phase.matches.find((m) => m.id === phase.bracket?.thirdPlaceMatchId) : undefined;
    if (phase.type === 'single_elim' && phase.status !== 'planned') {
      // Schon ausgelost: das Spiel um Platz 3 absagen bzw. zurückholen
      if (!third) {
        if (patch.thirdPlaceMatch) throw new EngineError('Das KO wurde ohne Spiel um Platz 3 ausgelost — nachträglich nur durch neues Auslosen');
      } else if (!patch.thirdPlaceMatch) {
        if (third.status === 'running') throw new EngineError('Das Spiel um Platz 3 läuft schon');
        if (third.status === 'finished' || third.status === 'walkover') throw new EngineError('Das Spiel um Platz 3 ist schon gespielt');
        third.status = 'cancelled';
        warnings.push('Spiel um Platz 3 abgesagt — beide Halbfinal-Verlierer teilen sich Platz 3');
      } else if (third.status === 'cancelled') {
        third.status = third.a.kind === 'team' && third.b.kind === 'team' ? 'ready' : 'open';
      }
    }
    cfg.thirdPlaceMatch = patch.thirdPlaceMatch;
  }
  return warnings;
}

function validatePhaseConfig(comp: Competition, config: PhaseConfig): void {
  if (config.type === 'groups') {
    if ('groupSize' in config.layout && config.layout.groupSize < 2) throw new EngineError('Gruppengröße mindestens 2');
    if ('groupCount' in config.layout && config.layout.groupCount < 1) throw new EngineError('Mindestens eine Gruppe');
    if (config.qualifiersPerGroup < 0) throw new EngineError('Qualifikanten je Gruppe ≥ 0');
    if (config.partners && 'groupSize' in config.layout && config.layout.groupSize < 4) throw new EngineError('Wechselnde Partner: mindestens 4 Spieler je Gruppe');
  } else if (config.type === 'single_elim') {
    if (config.seedingSource === 'previousPhase' && comp.phases.length === 0) throw new EngineError('Keine Vorphase vorhanden — Setzung aus Setzliste oder Zufall wählen');
  }
}

function resetDraw(phase: Phase): void {
  phase.groups = [];
  phase.matches = [];
  delete phase.bracket;
  delete phase.koRounds;
  delete phase.draw;
  phase.frozen = false;
  phase.status = 'planned';
}

function groupMatches(t: Tournament, comp: Competition, phase: Phase, group: Group, doubleRound: boolean): Match[] {
  const cfg = phase.config as GroupsConfig;
  if (cfg.partners) return partnerGroupMatches(t, phase, group);
  const clubOf = (id: Id): string | undefined => clubKey(comp.teams.find((x) => x.id === id)?.club);
  let pairings = roundRobin(group.teamIds, { doubleRound });
  if (cfg.noIntraClubMatches) {
    // Liga-Modus: keine Spiele innerhalb derselben Mannschaft
    pairings = pairings.filter((p) => {
      const ca = clubOf(p.a);
      return !ca || ca !== clubOf(p.b);
    });
  }
  if (cfg.skipPairings?.length) {
    // Saison (docs/13 §4): Begegnungen, die an einem früheren Spieltag schon ausgetragen wurden, kommen nicht noch einmal
    const schon = new Set(cfg.skipPairings);
    const keyOf = (id: Id): string => teamKey(comp.teams.find((x) => x.id === id) ?? { name: id });
    pairings = pairings.filter((p) => !schon.has(pairingKey(keyOf(p.a), keyOf(p.b))));
  }
  return pairings.map((p) => ({
    id: nextId(t, `m:${phase.id}`),
    phaseId: phase.id,
    groupId: group.id,
    round: p.round,
    number: 0,
    a: { kind: 'team', teamId: p.a },
    b: { kind: 'team', teamId: p.b },
    status: 'ready' as const,
    priority: 0,
  }));
}

/** Wechselnde Partner: Doppel aus zwei Einzelspielern, je Spiel neu (schedule/partner.ts) */
export function partnerGroupMatches(t: Tournament, phase: Phase, group: Group): Match[] {
  const plan = partnerSchedule(group.teamIds, (phase.config as GroupsConfig).partners ?? 'once');
  return plan.matches.map((p) => ({
    id: nextId(t, `m:${phase.id}`),
    phaseId: phase.id,
    groupId: group.id,
    round: p.round,
    number: 0,
    a: { kind: 'team', teamId: p.a[0], partnerId: p.a[1] },
    b: { kind: 'team', teamId: p.b[0], partnerId: p.b[1] },
    status: 'ready' as const,
    priority: 0,
  }));
}

/** Laufende Nummern: Runde für Runde, Gruppen abwechselnd (damit Gruppen parallel laufen) */
export function renumber(phase: Phase): void {
  const sorted = phase.matches.slice().sort((x, y) => x.round - y.round || (x.groupId ?? '').localeCompare(y.groupId ?? '') || x.id.localeCompare(y.id, undefined, { numeric: true }));
  sorted.forEach((m, i) => {
    m.number = i + 1;
  });
  phase.matches = sorted;
}

function groupWarnings(comp: Competition, phase: Phase): string[] {
  const cfg = phase.config as GroupsConfig;
  const clubOf = (id: Id): string | undefined => clubKey(comp.teams.find((x) => x.id === id)?.club);
  const out: string[] = [];
  if (cfg.partners) {
    for (const g of phase.groups) {
      if (g.teamIds.length < 4) out.push(`Gruppe ${g.name}: Wechselnde Partner brauchen mindestens 4 Spieler — keine Spiele`);
      else if (cfg.partners === 'once') {
        const rest = partnerSchedule(g.teamIds, 'once').unpaired;
        for (const [x, y] of rest)
          out.push(`Gruppe ${g.name}: ${teamOf(comp, x).name} und ${teamOf(comp, y).name} spielen nie zusammen (bei ${g.teamIds.length} Spielern geht es nicht auf — glatt mit 4, 5, 8 oder 9)`);
      }
    }
  }
  for (const g of phase.groups) {
    for (let i = 0; i < g.teamIds.length; i++) {
      for (let j = i + 1; j < g.teamIds.length; j++) {
        const a = g.teamIds[i] as Id;
        const b = g.teamIds[j] as Id;
        const apart = cfg.constraints.keepApart.some(([x, y]) => (x === a && y === b) || (x === b && y === a));
        const club = cfg.constraints.avoidSameClub && clubOf(a) && clubOf(a) === clubOf(b);
        if (apart || club) out.push(`Gruppe ${g.name}: ${teamOf(comp, a).name} und ${teamOf(comp, b).name} ${apart ? 'sollten getrennt sein' : `(beide ${clubOf(a)})`}`);
      }
    }
  }
  return out;
}

function drawPhase(t: Tournament, phaseId: Id, seedArg: string | undefined): string[] {
  const { comp, phase } = phaseOf(t, phaseId);
  if (phase.frozen) throw new EngineError('Auslosung ist eingefroren');
  if (phase.status === 'running' || phase.status === 'finished') throw new EngineError('Phase läuft schon');
  if (phase.matches.some((m) => m.status === 'finished' || m.status === 'walkover' || m.status === 'running')) throw new EngineError('Es gibt schon Ergebnisse');
  const prev = comp.phases.find((p) => p.order === phase.order - 1);
  const warnings: string[] = [];
  const drawNo = (t.counters[`draw:${phase.id}`] = (t.counters[`draw:${phase.id}`] ?? 0) + 1);
  const seed = seedArg ?? `${t.seed}:${phase.id}:${drawNo}`;
  const rng = createRng(seed);
  resetDraw(phase);

  if (phase.config.type === 'groups') {
    if (prev && prev.status !== 'finished') warnings.push(`Vorphase ${prev.order} ist nicht abgeschlossen`);
    const cfg = phase.config;
    const teams = eligibleTeams(comp.teams).filter((x) => !cfg.checkedInOnly || x.checkedIn);
    if (teams.length < 2)
      throw new EngineError(cfg.checkedInOnly ? 'Weniger als 2 Teams eingecheckt — erst einchecken (Teams → Check-in) oder „nur eingecheckte Teams auslosen" ausschalten' : 'Mindestens 2 Teams nötig');
    const ranked = rankTeams(teams, cfg.seeding, rng);
    if (cfg.groupByTeamNumber) {
      /*
       * Leistungsgruppen (docs/13 §7): die Nummer im Namen ist die Gruppe —
       * keine Verteilung, keine Nebenbedingungen. Gruppe „1" sind alle Einsen.
       */
      const nachNummer = new Map<number, Id[]>();
      for (const id of ranked) {
        const nr = teamNumber(comp.teams.find((x) => x.id === id)?.name ?? '') ?? 0;
        nachNummer.set(nr, [...(nachNummer.get(nr) ?? []), id]);
      }
      const nummern = [...nachNummer.keys()].sort((a, b) => (a || Infinity) - (b || Infinity));
      phase.groups = nummern.map((nr) => ({ id: nextId(t, `g:${phase.id}`), name: leistungsgruppenName(nr), teamIds: nachNummer.get(nr) as Id[] }));
      for (const g of phase.groups) phase.matches.push(...groupMatches(t, comp, phase, g, cfg.doubleRound));
      renumber(phase);
      const ohne = nachNummer.get(0)?.length ?? 0;
      if (ohne > 0) warnings.push(`${ohne} Team${ohne === 1 ? '' : 's'} ohne Nummer im Namen — eigene Gruppe „${leistungsgruppenName(0)}"`);
      for (const g of phase.groups) if (g.teamIds.length < 2) warnings.push(`Gruppe ${g.name} hat nur ein Team — keine Spiele`);
      warnings.push(...groupWarnings(comp, phase));
      phase.draw = { seed, input: ranked, algorithmVersion: ALGORITHM_VERSION };
    } else {
      const layout = groupLayout(ranked.length, cfg.partners && 'groupSize' in cfg.layout ? partnerLayoutSpec(ranked.length, cfg.layout.groupSize) : cfg.layout);
      let groups = distribute(ranked, layout, cfg.distribution, rng);
      const clubOf = (id: Id): string | undefined => clubKey(comp.teams.find((x) => x.id === id)?.club);
      const repaired = repairConstraints(groups, { clubOf, avoidSameClub: cfg.constraints.avoidSameClub, keepApart: cfg.constraints.keepApart }, rng);
      groups = repaired.groups;
      phase.groups = groups.map((teamIds, i) => ({ id: nextId(t, `g:${phase.id}`), name: groupName(i), teamIds }));
      for (const g of phase.groups) phase.matches.push(...groupMatches(t, comp, phase, g, cfg.doubleRound));
      renumber(phase);
      if (layout.uneven) warnings.push(`Gruppen ungleich groß (${layout.summary}) — gruppenübergreifender Vergleich wird pro Spiel gewertet`);
      warnings.push(...groupWarnings(comp, phase));
      phase.draw = { seed, input: ranked, algorithmVersion: ALGORITHM_VERSION };
    }
  } else if (phase.config.type === 'single_elim') {
    const cfg = phase.config;
    let qualifiers: Qualifier[];
    if (cfg.seedingSource === 'previousPhase') {
      if (!prev) throw new EngineError('Keine Vorphase');
      if (prev.status !== 'finished') throw new EngineError('Vorphase ist nicht abgeschlossen');
      qualifiers = qualifiersFromGroups(comp, prev);
    } else {
      const ranked = rankTeams(eligibleTeams(comp.teams), cfg.seedingSource, rng);
      qualifiers = ranked.map((teamId) => ({ teamId, tier: 1 }));
    }
    if (qualifiers.length < 2) throw new EngineError('Mindestens 2 Qualifikanten nötig');
    const size = qualifiers.length <= 2 ? 2 : 1 << Math.ceil(Math.log2(qualifiers.length));
    const placed = placeQualifiers(qualifiers, size, cfg.separateGroups, rng);
    const built = buildSingleElim(
      phase.id,
      placed.map((q) => q?.teamId ?? null),
      {
        thirdPlaceMatch: cfg.thirdPlaceMatch,
        nextId: () => nextId(t, `m:${phase.id}`),
        firstNumber: 1,
      },
    );
    phase.bracket = built.bracket;
    phase.matches = built.matches;
    const byes = size - qualifiers.length;
    if (byes > 0) warnings.push(`${byes} Freilos${byes === 1 ? '' : 'e'} an die bestgesetzten Teams`);
    phase.draw = { seed, input: qualifiers.map((q) => q.teamId), algorithmVersion: ALGORITHM_VERSION };
  } else if (phase.config.type === 'ko_rounds') {
    // KO-Runden brauchen keine Auslosung: jede Runde stellt die Leitung selbst
    // zusammen (`ko.startRound`). Der Zug hier hält nur den Seed fest.
    phase.draw = { seed, input: [], algorithmVersion: ALGORITHM_VERSION };
    warnings.push('KO-Runden werden einzeln gestartet — keine Auslosung nötig');
  } else {
    throw new EngineError(`Phasentyp ${phase.type} ist noch nicht umgesetzt`);
  }
  phase.status = 'drawn';
  autoWalkovers(t, comp, phase, 0);
  return warnings;
}

function groupName(i: number): string {
  return i < 26 ? String.fromCharCode(65 + i) : `${groupName(Math.floor(i / 26) - 1)}${groupName(i % 26)}`;
}

/** Leistungsgruppe: die Nummer selbst; 0 steht für „ohne Nummer" */
function leistungsgruppenName(nr: number): string {
  return nr === 0 ? 'ohne Nr.' : String(nr);
}

/**
 * Gruppe für ein nachgemeldetes Team: mit Leistungsgruppen die seiner Nummer
 * (notfalls neu angelegt, an der richtigen Stelle), sonst die kleinste.
 */
export function gruppeFuer(t: Tournament, phase: Phase, team: Team): Group {
  const cfg = phase.config as GroupsConfig;
  if (cfg.groupByTeamNumber) {
    const name = leistungsgruppenName(teamNumber(team.name) ?? 0);
    const vorhanden = phase.groups.find((g) => g.name === name);
    if (vorhanden) return vorhanden;
    const neu: Group = { id: nextId(t, `g:${phase.id}`), name, teamIds: [] };
    phase.groups.push(neu);
    const rang = (g: Group) => (g.name === 'ohne Nr.' ? Infinity : Number(g.name));
    phase.groups.sort((a, b) => rang(a) - rang(b));
    return neu;
  }
  return [...phase.groups].sort((x, y) => x.teamIds.length - y.teamIds.length)[0] as Group;
}

/**
 * Qualifikanten aus einer Gruppenphase (docs/04 §6): alle Gruppensieger
 * (untereinander nach Tabellenkriterien), dann alle Zweiten usw., dann die
 * besten Nächstplatzierten.
 */
export function qualifiersFromGroups(comp: Competition, prev: Phase): Qualifier[] {
  if (prev.type !== 'groups') throw new EngineError('Setzung aus Vorphase nur nach Gruppenphase möglich');
  const cfg = prev.config as GroupsConfig;
  const rules = rulesOf(comp, prev);
  const uneven = new Set(prev.groups.map((g) => g.teamIds.length)).size > 1;
  const tables = prev.groups.map((g) => {
    const opts: Parameters<typeof computeStandings>[3] = { qualifiers: cfg.qualifiersPerGroup };
    if (g.lotOrder) opts.lotOrder = g.lotOrder;
    let rows = computeStandings(
      g.teamIds,
      prev.matches.filter((m) => m.groupId === g.id),
      rules,
      opts,
    );
    if (uneven && cfg.stripLastWhenUneven && rows.length > 0) {
      const last = rows[rows.length - 1] as (typeof rows)[number];
      rows = computeStandings(
        g.teamIds,
        prev.matches.filter((m) => m.groupId === g.id),
        rules,
        { ...opts, excludeOpponents: [last.teamId] },
      );
    }
    return { group: g, rows };
  });
  for (const tbl of tables) {
    const open = tbl.rows.find((r) => r.needsLot && r.rank <= cfg.qualifiersPerGroup + 1);
    if (open) throw new EngineError(`Gruppe ${tbl.group.name}: Gleichstand auf Platz ${open.rank} muss gelost werden`);
  }
  // Platzierung vor Punkten: Stufe für Stufe gruppenübergreifend (standings/placement.ts)
  const out: Qualifier[] = [];
  for (let tier = 1; tier <= cfg.qualifiersPerGroup; tier++) {
    for (const { candidate } of placementTier(tables, rules, tier)) out.push({ teamId: candidate.row.teamId, tier, groupId: candidate.groupId });
  }
  if (cfg.bestRemaining > 0) {
    const tier = cfg.qualifiersPerGroup + 1;
    for (const { candidate } of placementTier(tables, rules, tier).slice(0, cfg.bestRemaining)) out.push({ teamId: candidate.row.teamId, tier, groupId: candidate.groupId });
  }
  return out;
}

function finishPhase(t: Tournament, phaseId: Id, force: boolean): string[] {
  const { comp, phase } = phaseOf(t, phaseId);
  const warnings: string[] = [];
  if (phase.status === 'finished') return warnings;
  // KO-Runden werden nicht ausgelost, sondern einzeln gestartet (docs/notes/ko-runden.md)
  if (phase.type === 'ko_rounds' && (phase.koRounds?.length ?? 0) === 0) throw new EngineError('Es wurde noch keine KO-Runde gestartet');
  if (phase.status === 'planned') throw new EngineError('Phase wurde noch nicht ausgelost');
  const running = phase.matches.filter((m) => m.status === 'running');
  if (running.length > 0) throw new EngineError(`${running.length} Spiel(e) laufen noch`);
  const pending = phase.matches.filter((m) => m.status === 'ready' || m.status === 'open');
  if (pending.length > 0) {
    if (!force) throw new EngineError(`${pending.length} Spiel(e) sind noch offen`);
    for (const m of pending) m.status = 'cancelled';
    warnings.push(`${pending.length} offene Spiele abgesagt`);
  }
  if (phase.type === 'groups') {
    const cfg = phase.config as GroupsConfig;
    const rules = rulesOf(comp, phase);
    for (const g of phase.groups) {
      const opts: Parameters<typeof computeStandings>[3] = { qualifiers: cfg.qualifiersPerGroup };
      if (g.lotOrder) opts.lotOrder = g.lotOrder;
      const rows = computeStandings(
        g.teamIds,
        phase.matches.filter((m) => m.groupId === g.id),
        rules,
        opts,
      );
      const open = rows.find((r) => r.needsLot && r.rank <= cfg.qualifiersPerGroup + (cfg.bestRemaining > 0 ? 1 : 0));
      if (open && !force) throw new EngineError(`Gruppe ${g.name}: Gleichstand auf Platz ${open.rank} — bitte losen (group.resolveLot)`);
    }
  }
  phase.status = 'finished';
  if (comp.phases.every((p) => p.status === 'finished')) comp.status = 'finished';
  if (t.competitions.every((c) => c.status === 'finished')) t.status = 'finished';
  return warnings;
}
